import React, { useContext } from 'react'
import classNames from 'classnames'
import MainNavigationContext from '@toasttab/shared/src/data/HeaderNavigation/context/MainNavigationContext'
import { SubMenus as DesktopSubMenus } from '@toasttab/shared/src/components/organisms/Navigation/Desktop/SubMenus'
import {
  ParentMenuProps,
  ParentMenusProps
} from '@toasttab/shared/src/data/HeaderNavigation/MainNavigationMenu.d'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'

export const ParentMenus = ({
  navigationData,
  showDropdowns
}: ParentMenusProps) => {
  const { selectedParentMenu, setSelectedParentMenu } = useContext(
    MainNavigationContext
  )
  const localizationContext = useContext(I18nextContext)
  const windowSize = useWindowSize()
  const pageContext = usePageContext()
  const packageName = pageContext?.packageName || ''

  const generateParentMenu = (menu: ParentMenuProps) => {
    const getParentMenuCss = (parent: string, selectedParentMenu: string) => {
      return classNames(
        'group/parent-item font-sansPro text-base text-default font-semibold border-b-4 hover:border-blue-75 focus-within:border-blue-75 pl-2 mr-3 pb-[18px] hidden min-[992px]:block',
        {
          'border-transparent': parent !== selectedParentMenu,
          'border-blue-75': parent === selectedParentMenu
        }
      )
    }

    const isAriaExpanded = (parent: string) => {
      let expanded = parent === selectedParentMenu ? true : false
      return expanded
    }

    if (menu && menu.subMenus) {
      return (
        <li
          className={getParentMenuCss(menu.label, selectedParentMenu)}
          role='none'
          key={`desktop-parent-dropdown-${menu.label}}`}
          onClick={() => setSelectedParentMenu(menu.label)}
          onMouseEnter={() => setSelectedParentMenu(menu.label)}
          onMouseLeave={() => setSelectedParentMenu(null)}
        >
          <button
            className={`${menu.heapClass}`}
            role='menuitem'
            aria-haspopup='true'
            aria-expanded={isAriaExpanded(menu.label)}
            aria-controls={`${menu.label.replace(/\s/g, '-')}-menu`}
          >
            {menu.label}
            <span className='block float-right h-6 w-6 mx-3 after:content-icon-caret-down-blue group-hover/parent-item:rotate-180 group-focus/parent-item:rotate-180'></span>
          </button>
          <DesktopSubMenus menu={menu} />
        </li>
      )
    } else {
      const menuHref = localizeLink(
        menu.url,
        localizationContext.language,
        localizationContext
      )
      return (
        <li
          className={`${getParentMenuCss(menu.label, selectedParentMenu)} pr-2`}
          role='none'
          key={`desktop-parent-link-${menu.label}}`}
        >
          <a
            className={`${menu.heapClass} text-default`}
            role='menuitem'
            href={menuHref}
          >
            {menu.label}
          </a>
        </li>
      )
    }
  }

  const navClasses = classNames({
    'self-start': !showDropdowns
  })

  return (
    <nav
      aria-label='Toast Desktop Navigation'
      aria-hidden={!(windowSize.width >= 992)}
      className={navClasses}
    >
      <ul
        className='col-span-12 flex flex-nowrap justify-start ml-4 md:ml-6 min-[992px]:ml-7 min-[1440px]:ml-[156px]'
        role='menubar'
        aria-label='Toast Main Navigation'
      >
        <li
          className='logo inline-block w-[74px] min-[992px]:w-[107px] mr-6 pb-[18px] min-[992px]:pb-0'
          role='none'
          key='desktop-parent-link-home'
        >
          <a
            role='menuitem'
            href={
              packageName === 'support'
                ? 'https://pos.toasttab.com'
                : localizeLink(
                    '/',
                    localizationContext.language,
                    localizationContext
                  )
            }
          >
            <img alt='Toast' src={resolvePrefix('/svg/img-toast-logo.svg')} />
          </a>
        </li>
        {showDropdowns &&
          navigationData.parentMenus.map((menu: any) =>
            generateParentMenu(menu)
          )}
      </ul>
    </nav>
  )
}
